/**
 * Light/dark theme (ADR-0013). The choice lives in localStorage; "system"
 * (the default) follows `prefers-color-scheme`. index.html applies the class
 * before first paint so the page never flashes the wrong theme.
 */
import { useSyncExternalStore } from "react"

export type ThemeChoice = "light" | "dark" | "system"
export type Theme = "light" | "dark"

const KEY = "uachieve.theme"
const THEME_COLOR: Record<Theme, string> = { light: "#F3F1EE", dark: "#0F1513" }
const listeners = new Set<() => void>()

function readChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY)
    return v === "light" || v === "dark" ? v : "system"
  } catch {
    return "system"
  }
}

function systemTheme(): Theme {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

export function resolveTheme(choice: ThemeChoice = readChoice()): Theme {
  return choice === "system" ? systemTheme() : choice
}

/** Apply the theme to <html> (class + color-scheme meta). */
export function applyTheme(theme: Theme = resolveTheme()) {
  const root = document.documentElement
  root.classList.toggle("dark", theme === "dark")
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[theme])
}

export function setTheme(choice: ThemeChoice) {
  try {
    if (choice === "system") localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, choice)
  } catch {
    // Storage blocked: the choice still applies for this page view.
  }
  current = resolveTheme(choice)
  applyTheme(current)
  listeners.forEach((l) => l())
}

let current: Theme = typeof window === "undefined" ? "light" : resolveTheme()

function subscribe(listener: () => void) {
  listeners.add(listener)
  const media = window.matchMedia?.("(prefers-color-scheme: dark)")
  const onSystem = () => {
    if (readChoice() === "system") {
      current = systemTheme()
      applyTheme(current)
      listeners.forEach((l) => l())
    }
  }
  media?.addEventListener("change", onSystem)
  return () => {
    listeners.delete(listener)
    media?.removeEventListener("change", onSystem)
  }
}

/** The resolved theme, re-rendering when it changes. */
export function useTheme(): Theme {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => "light",
  )
}
